import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Plus, 
  Radio, 
  Trash2, 
  Edit2, 
  Filter,
  Loader2,
  Power,
  PowerOff,
  Sparkles,
  Search,
  Check,
  X
} from 'lucide-react';
import { FaTelegram } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MonitorsPage() {
  const [monitors, setMonitors] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form state
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedChannelTitle, setSelectedChannelTitle] = useState('');
  const [keywords, setKeywords] = useState('');
  const [smartMatch, setSmartMatch] = useState(false);
  const [nickname, setNickname] = useState('');
  const [notes, setNotes] = useState('');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [channelFilter, setChannelFilter] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [monitorsRes, channelsRes] = await Promise.all([
        axios.get(`${API}/monitors`, { withCredentials: true }),
        axios.get(`${API}/telegram/channels`, { withCredentials: true })
      ]);
      setMonitors(monitorsRes.data.monitors || []);
      setChannels(channelsRes.data.channels || []);
    } catch (error) {
      toast.error('Failed to load monitors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChannel = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a channel username');
      return;
    }
    
    setSearching(true);
    setSearchResult(null);
    
    try {
      const response = await axios.post(`${API}/telegram/search-channel`, null, {
        params: { channel_username: searchQuery.trim() },
        withCredentials: true
      });
      
      if (response.data.found) {
        setSearchResult(response.data);
        toast.success(`Found: ${response.data.title}`);
      } else {
        toast.error('Channel not found. Make sure the username is correct.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to search channel');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSearchResult = () => {
    if (searchResult) {
      setSelectedChannel(searchResult.username || searchResult.id);
      setSelectedChannelTitle(searchResult.title);
      toast.success(`Selected: ${searchResult.title}`);
    }
  };

  const handleSelectFromList = (channel) => {
    setSelectedChannel(channel.username || channel.id);
    setSelectedChannelTitle(channel.title);
  };

  const handleCreateMonitor = async () => {
    if (!selectedChannel) {
      toast.error('Please select a channel');
      return;
    }
    
    setProcessing(true);
    try {
      const keywordsList = keywords.split(',').map(k => k.trim()).filter(k => k);
      await axios.post(`${API}/monitors`, {
        channel_username: selectedChannel,
        keywords: keywordsList,
        smart_match: smartMatch,
        nickname: nickname || null,
        notes: notes || null
      }, { withCredentials: true });
      
      toast.success('Monitor created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create monitor');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateMonitor = async () => {
    if (!selectedMonitor) return;
    
    setProcessing(true);
    try {
      const keywordsList = keywords.split(',').map(k => k.trim()).filter(k => k);
      await axios.patch(`${API}/monitors/${selectedMonitor.monitor_id}`, {
        keywords: keywordsList,
        smart_match: smartMatch,
        nickname: nickname || null,
        notes: notes || null
      }, { withCredentials: true });
      
      toast.success('Monitor updated successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update monitor');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleActive = async (monitor) => {
    try {
      await axios.patch(`${API}/monitors/${monitor.monitor_id}`, {
        active: !monitor.active
      }, { withCredentials: true });
      
      toast.success(monitor.active ? 'Monitor paused' : 'Monitor activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update monitor');
    }
  };

  const handleDeleteMonitor = async (monitorId) => {
    try {
      await axios.delete(`${API}/monitors/${monitorId}`, { withCredentials: true });
      toast.success('Monitor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete monitor');
    }
  };

  const openEditDialog = (monitor) => {
    setSelectedMonitor(monitor);
    setKeywords(monitor.keywords?.join(', ') || '');
    setSmartMatch(monitor.smart_match || false);
    setNickname(monitor.nickname || '');
    setNotes(monitor.notes || '');
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedChannel('');
    setSelectedChannelTitle('');
    setKeywords('');
    setSmartMatch(false);
    setNickname('');
    setNotes('');
    setSelectedMonitor(null);
    setSearchQuery('');
    setSearchResult(null);
    setActiveTab('search');
    setChannelFilter('');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight" data-testid="monitors-page-title">
              Channel Monitors
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage the Telegram channels you&apos;re monitoring
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full gap-2" data-testid="create-monitor-btn">
                <Plus className="w-4 h-4" />
                New Monitor
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Monitor</DialogTitle>
                <DialogDescription>
                  Search for a channel or select from your subscriptions
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="search" className="gap-2">
                      <Search className="w-4 h-4" />
                      Search
                    </TabsTrigger>
                    <TabsTrigger value="list" className="gap-2">
                      <FaTelegram className="w-4 h-4" />
                      My Channels
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="search" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Search Channel by Username</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                          <Input
                            placeholder="channel_username"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value.replace('@', ''))}
                            className="pl-8"
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchChannel()}
                            data-testid="channel-search-input"
                          />
                        </div>
                        <Button 
                          onClick={handleSearchChannel}
                          disabled={searching || !searchQuery.trim()}
                          data-testid="search-channel-btn"
                        >
                          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Enter the public channel username (e.g., durov, telegram)
                      </p>
                    </div>
                    
                    {searchResult && (
                      <div 
                        className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                          selectedChannel === (searchResult.username || searchResult.id)
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={handleSelectSearchResult}
                        data-testid="search-result"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FaTelegram className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{searchResult.title}</p>
                            <p className="text-sm text-muted-foreground">@{searchResult.username}</p>
                          </div>
                          {selectedChannel === (searchResult.username || searchResult.id) && (
                            <Check className="w-5 h-5 text-primary" />
                          )}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="list" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Select from Your Channels</Label>
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Filter your channels..."
                          value={channelFilter}
                          onChange={(e) => setChannelFilter(e.target.value)}
                          className="pl-10"
                          data-testid="channel-filter-input"
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto space-y-2 pr-2">
                        {channels.filter(c => c.username || c.id).filter(c => 
                          !channelFilter || 
                          c.title?.toLowerCase().includes(channelFilter.toLowerCase()) ||
                          c.username?.toLowerCase().includes(channelFilter.toLowerCase())
                        ).length > 0 ? (
                          channels.filter(c => c.username || c.id).filter(c => 
                            !channelFilter || 
                            c.title?.toLowerCase().includes(channelFilter.toLowerCase()) ||
                            c.username?.toLowerCase().includes(channelFilter.toLowerCase())
                          ).map((channel) => (
                            <div
                              key={channel.id}
                              className={`p-3 rounded-lg border-2 transition-colors cursor-pointer ${
                                selectedChannel === (channel.username || channel.id)
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50'
                              }`}
                              onClick={() => handleSelectFromList(channel)}
                              data-testid={`channel-${channel.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <FaTelegram className="w-4 h-4 text-primary" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm truncate">{channel.title}</p>
                                    {channel.is_private && (
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">Private</Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {channel.username ? `@${channel.username}` : channel.type || 'Private channel'}
                                  </p>
                                </div>
                                {selectedChannel === (channel.username || channel.id) && (
                                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            {channelFilter ? 'No channels match your filter.' : 'No channels found. Connect Telegram first or use the search tab.'}
                          </p>
                        )}
                      </div>
                      {channels.length > 0 && (
                        <p className="text-xs text-muted-foreground text-center">
                          {channels.length} channel{channels.length !== 1 ? 's' : ''} available
                        </p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Selected Channel Display */}
                {selectedChannel && (
                  <div className="p-3 rounded-lg bg-success/10 border border-success/20">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-success" />
                      <span className="text-sm font-medium">Selected:</span>
                      <span className="text-sm">{selectedChannelTitle || selectedChannel}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="nickname">Nickname (optional)</Label>
                  <Input
                    id="nickname"
                    placeholder="My favorite crypto channel"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    data-testid="nickname-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    A custom name to help you identify this monitor
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <textarea
                    id="notes"
                    placeholder="Add any notes about this channel..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="notes-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="keywords">Keywords (comma separated)</Label>
                  <Input
                    id="keywords"
                    placeholder="bitcoin, crypto, trading"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    data-testid="keywords-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty to receive all messages from this channel
                  </p>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Smart Matching</p>
                      <p className="text-xs text-muted-foreground">Use AI to match related terms</p>
                    </div>
                  </div>
                  <Switch 
                    checked={smartMatch} 
                    onCheckedChange={setSmartMatch}
                    data-testid="smart-match-toggle"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateMonitor} 
                  disabled={processing || !selectedChannel}
                  className="rounded-full"
                  data-testid="create-monitor-submit"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Monitor
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Monitors Grid */}
        {monitors.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {monitors.map((monitor, index) => (
                <motion.div
                  key={monitor.monitor_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`card-hover border-border/50 ${!monitor.active ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FaTelegram className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">
                              {monitor.nickname || monitor.channel_title}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {monitor.nickname ? monitor.channel_title + ' • ' : ''}@{monitor.channel_username}
                            </CardDescription>
                          </div>
                        </div>
                        <Badge variant={monitor.active ? 'default' : 'secondary'} className="text-xs">
                          {monitor.active ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {monitor.notes && (
                        <div className="p-2 rounded bg-muted/50 text-xs text-muted-foreground">
                          {monitor.notes}
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <Filter className="w-3 h-3" /> Keywords
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {monitor.keywords?.length > 0 ? (
                            monitor.keywords.map((kw) => (
                              <Badge key={kw} variant="outline" className="text-xs">
                                {kw}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-muted-foreground">All messages</span>
                          )}
                        </div>
                      </div>
                      {monitor.smart_match && (
                        <div className="flex items-center gap-1 text-xs text-primary">
                          <Sparkles className="w-3 h-3" />
                          Smart matching enabled
                        </div>
                      )}
                      <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(monitor)}
                          className="flex-1 gap-1"
                          data-testid={`toggle-monitor-${monitor.monitor_id}`}
                        >
                          {monitor.active ? (
                            <><PowerOff className="w-4 h-4" /> Pause</>
                          ) : (
                            <><Power className="w-4 h-4" /> Activate</>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(monitor)}
                          data-testid={`edit-monitor-${monitor.monitor_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteMonitor(monitor.monitor_id)}
                          data-testid={`delete-monitor-${monitor.monitor_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-border/50">
              <CardContent className="py-16 text-center">
                <Radio className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No monitors yet</h3>
                <p className="text-muted-foreground mb-6">
                  Create your first monitor to start receiving alerts
                </p>
                <Button onClick={() => setDialogOpen(true)} className="rounded-full gap-2">
                  <Plus className="w-4 h-4" />
                  Create Monitor
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Monitor</DialogTitle>
              <DialogDescription>
                Update settings for {selectedMonitor?.nickname || selectedMonitor?.channel_title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-nickname">Nickname</Label>
                <Input
                  id="edit-nickname"
                  placeholder="My favorite crypto channel"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  data-testid="edit-nickname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <textarea
                  id="edit-notes"
                  placeholder="Add any notes about this channel..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="edit-notes-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-keywords">Keywords (comma separated)</Label>
                <Input
                  id="edit-keywords"
                  placeholder="bitcoin, crypto, trading"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  data-testid="edit-keywords-input"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Smart Matching</p>
                    <p className="text-xs text-muted-foreground">Use AI to match related terms</p>
                  </div>
                </div>
                <Switch 
                  checked={smartMatch} 
                  onCheckedChange={setSmartMatch}
                  data-testid="edit-smart-match-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateMonitor} 
                disabled={processing}
                className="rounded-full"
                data-testid="update-monitor-submit"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Monitor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
